import React, { useState } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import ContactPopup from '@/components/ContactPopup';
import MobileSlider from '@/components/MobileSlider';
import TestimonialSlider from '@/components/TestimonialSlider';
import { Shield, FileCheck, CheckCircle, ChevronDown, ArrowRight } from 'lucide-react';

const ITCommercialLaw = () => {
  const [showPopup, setShowPopup] = useState(false);

  const FAQItem = ({ question, answer }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
      <div className="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-lg">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-6 py-5 flex items-center justify-between text-left transition-colors hover:bg-gray-50"
        >
          <span className="font-semibold text-lg pr-4" style={{ color: 'var(--text-dark)' }}>
            {question}
          </span>
          <ChevronDown 
            className={`text-emerald-600 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
            size={24} 
          />
        </button>
        <div 
          className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96' : 'max-h-0'}`}
        >
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
            <p className="text-gray-600 leading-relaxed">{answer}</p>
          </div>
        </div>
      </div>
    );
  };

  const services = [
    'Comprehensive consultation',
    'Document preparation and filing',
    'Legal compliance review',
    'Expert guidance throughout the process',
    'Follow-up support and assistance',
    'Status tracking and updates',
  ];

  const faqs = [
    {
      question: 'What is included in this service?',
      answer: 'Our service includes complete end-to-end support with expert guidance, document preparation, filing assistance, and ongoing support throughout the entire process.',
    },
    {
      question: 'How long does the process take?',
      answer: 'Processing time varies depending on the complexity and jurisdiction, typically ranging from a few weeks to several months. We keep you informed every step of the way.',
    },
    {
      question: 'What documents do I need to provide?',
      answer: 'Required documents vary by service type. Our team will provide you with a detailed checklist of all necessary documentation during your initial consultation.',
    },
  ];

  return (
    <div>
      <Navigation />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center bg-gradient-to-br from-emerald-50 via-purple-50 to-pink-50 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-emerald-300 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-purple-300 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000"></div>
        
        <div className="container mx-auto px-4 py-12 md:py-16 lg:py-20 relative z-10">
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-gradient-to-r from-emerald-600 to-purple-600 text-white text-sm font-semibold rounded-full mb-4">
                Legal Services
              </div>
              <h1 className="text-4xl sm:text-3xl sm:text-4xl lg:text-5xl lg:text-4xl sm:text-3xl sm:text-4xl lg:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                <span className="bg-gradient-to-r from-emerald-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                  IT & Commercial Law
                </span>
                <br />
                <span style={{ color: 'var(--text-dark)' }}>Services</span>
              </h1>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Expert legal services for IT and commercial matters.
              </p>
              <button 
                onClick={() => setShowPopup(true)}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-emerald-600 to-purple-600 text-white font-bold rounded-full hover:shadow-xl hover:scale-105 transition-all group"
              >
                Get Started Today
                <ArrowRight className="ml-2 group-hover:translate-x-2 transition-transform" size={20} />
              </button>
            </div>
            <div className="relative">
              <div className="relative z-10 rounded-3xl overflow-hidden shadow-2xl">
                <img src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b" alt="IT & Commercial Law" className="w-full" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wide">Services</span>
              <h2 className="text-4xl sm:text-3xl sm:text-4xl lg:text-5xl font-bold mt-2 mb-4" style={{ color: 'var(--text-dark)' }}>
                What We Offer
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {services.map((service, index) => (
                <div key={index} className="flex items-center bg-white p-5 rounded-xl shadow-lg hover:shadow-xl transition-all border-2 border-gray-100 hover:border-emerald-200">
                  <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-purple-600 rounded-full flex items-center justify-center mr-4 flex-shrink-0">
                    <FileCheck className="text-white" size={18} />
                  </div>
                  <span className="text-gray-700 font-medium">{service}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Informative Section 1 */}
      <section className="section-padding bg-gradient-to-br from-gray-50 to-emerald-50">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/2 space-y-6 order-2 lg:order-1 w-full">
              <div className="inline-block px-4 py-2 bg-emerald-100 rounded-full mb-2">
                <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wide">Professional Service</span>
              </div>
              <h2 className="text-4xl sm:text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight" style={{ color: 'var(--text-dark)' }}>
                Expert <span className="text-emerald-600">Guidance</span> Every Step
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our experienced team provides comprehensive support throughout your entire journey. We handle the complex legal requirements so you can focus on what matters most.
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="text-emerald-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Professional consultation and planning</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-emerald-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Complete documentation and filing</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="text-emerald-600 mr-3 mt-1 flex-shrink-0" size={20} />
                  <span className="text-gray-700">Ongoing support and guidance</span>
                </li>
              </ul>
              <button 
                onClick={() => setShowPopup(true)}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-emerald-600 to-purple-600 text-white font-bold rounded-full hover:shadow-xl hover:scale-105 transition-all group mt-4"
              >
                Learn More
                <ArrowRight className="ml-2 group-hover:translate-x-2 transition-transform" size={20} />
              </button>
            </div>
            <div className="lg:w-1/2">
              <div className="relative">
                <div className="absolute -top-6 -left-6 w-24 h-24 bg-emerald-200 rounded-full blur-3xl opacity-60"></div>
                <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-purple-200 rounded-full blur-3xl opacity-60"></div>
                <img src="https://images.unsplash.com/photo-1518770660439-4636190af475" 
                  alt="Professional Service" 
                  className="relative rounded-3xl shadow-2xl w-full h-auto object-cover border-8 border-white"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Informative Section 2 */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row-reverse items-center gap-12">
            <div className="lg:w-1/2 space-y-6 order-2 lg:order-1 w-full">
              <div className="inline-block px-4 py-2 bg-purple-100 rounded-full mb-2">
                <span className="text-purple-600 font-semibold text-sm uppercase tracking-wide">Why Choose Us</span>
              </div>
              <h2 className="text-4xl sm:text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight" style={{ color: 'var(--text-dark)' }}>
                Your Success is <span className="text-purple-600">Our Priority</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                With years of experience and a track record of success, we provide the expertise and personalized attention your case deserves. Trust us to navigate the complexities while you focus on your goals.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our dedicated team ensures every detail is handled with precision and care, providing you with peace of mind throughout the entire process.
              </p>
              <button 
                onClick={() => setShowPopup(true)}
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-full hover:shadow-xl hover:scale-105 transition-all group mt-4"
              >
                Get Started
                <ArrowRight className="ml-2 group-hover:translate-x-2 transition-transform" size={20} />
              </button>
            </div>
            <div className="lg:w-1/2">
              <div className="relative">
                <div className="absolute -top-6 -right-6 w-24 h-24 bg-purple-200 rounded-full blur-3xl opacity-60"></div>
                <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-pink-200 rounded-full blur-3xl opacity-60"></div>
                <img src="https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5" 
                  alt="Success Story" 
                  className="relative rounded-3xl shadow-2xl w-full h-auto object-cover border-8 border-white"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section-padding bg-gradient-to-br from-gray-50 to-emerald-50">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-12">
            <div className="lg:w-1/2">
              <div className="mb-8">
                <div className="inline-block px-4 py-2 bg-emerald-100 rounded-full mb-4">
                  <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wide">FAQs</span>
                </div>
                <h2 className="text-4xl sm:text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" style={{ color: 'var(--text-dark)' }}>
                  Frequently Asked Questions
                </h2>
                <p className="text-lg text-gray-600">
                  Find answers to common questions about our services.
                </p>
              </div>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <FAQItem key={index} question={faq.question} answer={faq.answer} />
                ))}
              </div>
            </div>
            <div className="lg:w-1/2 flex items-start">
              <div className="relative w-full sticky top-8">
                <div className="absolute -top-6 -right-6 w-32 h-32 bg-emerald-200 rounded-full blur-3xl opacity-60"></div>
                <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-purple-200 rounded-full blur-3xl opacity-60"></div>
                <img src="https://images.unsplash.com/photo-1531973576160-7125cd663d86" 
                  alt="Consultation" 
                  className="relative rounded-3xl shadow-2xl w-full h-full object-cover border-8 border-white"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-gradient-to-br from-emerald-50 to-purple-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wide">Testimonials</span>
            <h2 className="text-4xl sm:text-3xl sm:text-4xl lg:text-5xl font-bold mt-2 mb-4" style={{ color: 'var(--text-dark)' }}>
              Client Success Stories
            </h2>
          </div>
          <TestimonialSlider />
        </div>
      </section>

      <Footer />
      {showPopup && <ContactPopup onClose={() => setShowPopup(false)} source="itcommerciallaw" />}
    </div>
  );
};

export default ITCommercialLaw;
